import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './LoginPage.css';
import TopBar from './TopBar';
import SocialButton from './SocialButton';
import WebViewWarningModal from './WebViewWarningModal';
import { GoogleIcon, EmailIcon } from './Icons';
import { loginWithGoogle, loginWithEmail, createAccount } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { detectBrowserEnvironment } from '../utils/browserDetection';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rememberMe, setRememberMe] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showWebViewWarning, setShowWebViewWarning] = useState(false);
  const [browserInfo, setBrowserInfo] = useState(null);

  const { checkUser } = useAuth();

  // Detect browser environment on mount
  useEffect(() => {
    try {
      const env = detectBrowserEnvironment();
      setBrowserInfo(env);

      // If in-app browser, show warning modal (non-blocking)
      if (env.isInAppBrowser) {
        console.warn('WebView/In-app browser detected:', env.browserName);
        setShowWebViewWarning(true);
      }

      // Show info message passed via navigation state, if any
      if (location.state?.message) {
        setInfoMessage(location.state.message);
      }
    } catch (err) {
      // Handle detection errors gracefully
      console.error('Browser detection error:', err);
      if (err?.code === 'WEBVIEW_DETECTED') {
        setShowWebViewWarning(true);
      }
    }
  }, [location]);

  // Handle Google OAuth Login
  const handleGoogleLogin = async () => {
    try {
      setError('');
      loginWithGoogle(rememberMe);
      // Note: User will be redirected to Google, then back to the app
    } catch (err) {
      setError('Google login failed. Please try again.');
      console.error('Google login error:', err);
    }
  };

  // Show email form
  const handleEmailClick = () => {
    setShowEmailForm(true);
    setError('');
  };

  // Handle email form submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Create new account
        await createAccount(email, password, name, rememberMe);
        // Show success message - user needs to verify email
        setInfoMessage('Account created successfully! Please check your inbox (and spam/junk folder) to verify your email before logging in.');
        setIsSignUp(false);
        setShowEmailForm(false);
        setEmail('');
        setPassword('');
        setName('');
      } else {
        // Login with existing account
        await loginWithEmail(email, password, rememberMe);
        // Refresh user state
        await checkUser();
        // Redirect to intended page or landing page
        const redirectTo = location.state?.redirectTo || '/';
        navigate(redirectTo);
      }
    } catch (err) {
      console.error('Auth error:', err);
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setError(err.message);
        setInfoMessage('Need a new verification link? Contact support or try creating a new account.');
      } else if (err.code === 401) {
        setError('Invalid email or password.');
      } else if (err.code === 409) {
        setError('An account with this email already exists.');
      } else if (err.message?.includes('password')) {
        setError('Password must be at least 8 characters long.');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleBackToOptions = () => {
    setShowEmailForm(false);
    setIsSignUp(false);
    setEmail('');
    setPassword('');
    setName('');
    setError('');
  };

  return (
    <div className="login-page">
      {/* Header with Logo */}
      <TopBar />

      {showWebViewWarning && browserInfo && (
        <WebViewWarningModal
          browserName={browserInfo.browserName}
          onClose={() => setShowWebViewWarning(false)}
        />
      )}

      {/* iOS ITP Warning */}
      {/iPhone|iPad|iPod/i.test(navigator.userAgent) && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px',
          lineHeight: '1.4',
          border: '1px solid #ffeeba',
          maxWidth: '400px',
          margin: '10px auto 20px auto'
        }}>
          <strong>📱 iPhone/Safari Users:</strong> If you're having trouble logging in, please disable "Prevent Cross-Site Tracking" in Settings → Safari → Privacy & Security. This is required for authentication to work.
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        <div className="login-container">
          {/* Title Section */}
          <div className="title-section">
            <h1 className="title">{showEmailForm ? (isSignUp ? 'Create Account' : 'Sign In') : 'Secured Login'}</h1>
            <p className="subtitle">
              {showEmailForm
                ? (isSignUp ? 'Create a new account to get started' : 'Enter your credentials to continue')
                : 'Sign Up or log in to continue'
              }
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Info Message */}
          {infoMessage && (
            <div className="info-message">
              {infoMessage}
            </div>
          )}

          {!showEmailForm ? (
            <>
              {/* Remember Me Checkbox */}
              <div className="remember-me-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">Remember me on this device for 60 days</span>
                </label>
              </div>

              {/* Social Login Buttons */}
              <div className="social-buttons">
                <SocialButton
                  icon={<GoogleIcon />}
                  text="Continue with Google"
                  onClick={handleGoogleLogin}
                />
                <SocialButton
                  icon={<EmailIcon />}
                  text="Continue with Email"
                  onClick={handleEmailClick}
                />
              </div>
            </>
          ) : (
            /* Email Form */
            <form onSubmit={handleEmailSubmit} className="email-form">
              {isSignUp && (
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-input"
                    placeholder="Enter your full name"
                    required={isSignUp}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder={isSignUp ? "Create a password (min. 8 characters)" : "Enter your password"}
                  minLength={8}
                  required
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>

              <div className="form-footer">
                {!isSignUp && (
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot password?
                  </Link>
                )}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setIsSignUp(!isSignUp)}
                >
                  {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={handleBackToOptions}
                >
                  ← Back to login options
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
